/**
 * Promemoria "Ricontatta" delle chat WhatsApp (regole in services/whatsapp/recontactRules.ts).
 * Chat collegata a un Lead/Cliente -> Task commerciale; non collegata -> whatsapp_chat_meta.
 */
import { create } from 'zustand';
import { supabase } from '../supabase';
import { buildSalesTaskInsert } from '@/components/whatsapp2/chat/salesTaskFromSuggestion';
import {
  RECONTACT_SOURCE, indexRecontacts, recontactFromMeta, recontactFromTask, recontactTaskMetadata, recontactTaskTitle,
  type ChatRecontact, type RecontactMetaRow, type RecontactTaskRow,
} from '../services/whatsapp/recontactRules';

export interface ScheduleRecontactInput {
  chatKey: string;
  chatName: string;
  dueAt: Date;
  note: string | null;
  /** Lead/Cliente collegato alla chat: se c'e' si crea un Task commerciale. */
  clientId: string | null;
  userId: string;
  organizationId: string;
}

interface State {
  reminders: Map<string, ChatRecontact>;
  /** false se le colonne recontact_* di whatsapp_chat_meta non ci sono ancora (migration da applicare). */
  metaAvailable: boolean;
  loadedOrg: string | null;
  load: (organizationId: string) => Promise<void>;
  schedule: (input: ScheduleRecontactInput) => Promise<boolean>;
  complete: (chatKey: string, organizationId: string) => Promise<boolean>;
}

const MANCA_COLONNA = new Set(['42703', 'PGRST204']);

export const useChatRecontactStore = create<State>((set, get) => ({
  reminders: new Map(),
  metaAvailable: true,
  loadedOrg: null,

  load: async (organizationId) => {
    const tasks = await supabase
      .from('sales_activities')
      .select('id, due_date, description, created_at, client_id, lead_id, metadata, status, completed_at')
      .eq('organization_id', organizationId)
      .eq('metadata->>source', RECONTACT_SOURCE)
      .is('completed_at', null)
      .neq('status', 'completed')
      .limit(1000);
    let metaRows: RecontactMetaRow[] = [];
    let metaAvailable = get().metaAvailable;
    if (metaAvailable) {
      const meta = await supabase
        .from('whatsapp_chat_meta')
        .select('chat_jid, recontact_at, recontact_note, recontact_set_at')
        .eq('organization_id', organizationId)
        .not('recontact_at', 'is', null)
        .limit(1000);
      if (meta.error && MANCA_COLONNA.has(meta.error.code)) metaAvailable = false;
      else if (!meta.error) metaRows = (meta.data || []) as unknown as RecontactMetaRow[];
    }
    if (tasks.error) console.error('[Ricontatta] lettura Task commerciali:', tasks.error);
    const list = [
      ...((tasks.data || []) as unknown as RecontactTaskRow[]).map(recontactFromTask),
      ...metaRows.map(recontactFromMeta),
    ];
    set({ reminders: indexRecontacts(list), metaAvailable, loadedOrg: organizationId });
  },

  schedule: async (input) => {
    const setAt = new Date().toISOString();
    const note = input.note?.trim() || null;
    const existing = get().reminders.get(input.chatKey);
    let saved: ChatRecontact | null = null;

    if (existing?.taskId || (!existing && input.clientId)) {
      const metadata = recontactTaskMetadata(input.chatKey, note, setAt);
      if (existing?.taskId) {
        // Rimanda: stesso Task commerciale, nuova scadenza.
        const { error } = await supabase
          .from('sales_activities')
          .update({ due_date: input.dueAt.toISOString(), description: note || '', metadata, status: 'pending' })
          .eq('id', existing.taskId);
        if (error) { console.error('[Ricontatta] rimanda:', error); return false; }
        saved = { ...existing, dueAt: input.dueAt.toISOString(), note, setAt };
      } else {
        // Il titolo usa il nome della scheda collegata (Lead/Cliente), non quello della chat.
        const { data: client } = await supabase
          .from('clients')
          .select('business_name, contact_name')
          .eq('id', input.clientId as string)
          .maybeSingle();
        const row = {
          ...buildSalesTaskInsert({
            title: recontactTaskTitle(client?.business_name || client?.contact_name || input.chatName),
            description: note || '',
            dueDate: input.dueAt,
            clientId: input.clientId as string,
            userId: input.userId,
            organizationId: input.organizationId,
          }),
          metadata,
        };
        const { data, error } = await supabase.from('sales_activities').insert(row).select('id').single();
        if (error || !data) { console.error('[Ricontatta] nuovo Task commerciale:', error); return false; }
        saved = { chatKey: input.chatKey, dueAt: row.due_date, note, setAt, taskId: data.id, clientId: input.clientId };
      }
    } else {
      if (!get().metaAvailable) return false;
      const { error } = await supabase
        .from('whatsapp_chat_meta')
        .upsert(
          { organization_id: input.organizationId, chat_jid: input.chatKey, recontact_at: input.dueAt.toISOString(), recontact_note: note, recontact_set_at: setAt, updated_at: setAt },
          { onConflict: 'organization_id,chat_jid' },
        );
      if (error) {
        console.error('[Ricontatta] salvataggio sulla chat:', error);
        if (MANCA_COLONNA.has(error.code)) set({ metaAvailable: false });
        return false;
      }
      saved = { chatKey: input.chatKey, dueAt: input.dueAt.toISOString(), note, setAt, taskId: null, clientId: null };
    }

    const map = new Map(get().reminders);
    map.set(input.chatKey, saved);
    set({ reminders: map });
    return true;
  },

  complete: async (chatKey, organizationId) => {
    const r = get().reminders.get(chatKey);
    if (!r) return true;
    const now = new Date().toISOString();
    const { error } = r.taskId
      ? await supabase.from('sales_activities').update({ status: 'completed', completed_at: now }).eq('id', r.taskId)
      : await supabase
        .from('whatsapp_chat_meta')
        .update({ recontact_at: null, recontact_note: null, recontact_set_at: null, updated_at: now })
        .eq('organization_id', organizationId)
        .eq('chat_jid', chatKey);
    if (error) { console.error('[Ricontatta] chiusura:', error); return false; }
    const map = new Map(get().reminders);
    map.delete(chatKey);
    set({ reminders: map });
    // Un altro promemoria aperto sulla stessa chat (raro) ricompare alla prossima lettura.
    void get().load(organizationId);
    return true;
  },
}));
